import { initFirebase } from '../firebase'
import { collection, getDocs, query, where, limit } from 'firebase/firestore'

const { db } = initFirebase()

// Per-user learned "style profile" layer.
//   users/{uid}.styleProfile  — the merged profile (tone/length/depth/…, likes,
//     dislikes, sensitiveTopics, revision, updatedAt, lastReason)
//   system_logs (functionName == 'updateStyleProfile') — one record per update,
//     with metadata.reason / metadata.changes / metadata.revision / metadata.profile
//
// Both use single-field indexes only (auto-created), so no composite index setup:
//   users        : where styleProfile.revision > 0
//   system_logs  : where functionName == 'updateStyleProfile'
// Volume is low (feature just launched), so results are sorted client-side.

function toMs(ts) {
  if (!ts) return 0
  if (typeof ts === 'object' && typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts === 'object' && (ts.seconds || ts._seconds)) return (ts.seconds || ts._seconds) * 1000
  const t = new Date(ts).getTime()
  return isNaN(t) ? 0 : t
}

export class FirebaseStyleProfileService {
  // Users whose styleProfile is populated, newest-updated first.
  async getProfiles(max = 500) {
    const q = query(collection(db, 'users'), where('styleProfile.revision', '>', 0), limit(max))
    const snap = await getDocs(q)
    return snap.docs
      .map(d => {
        const data = d.data()
        return {
          uid: d.id,
          name: (data.properties && data.properties.name) || '',
          styleProfile: data.styleProfile || {}
        }
      })
      .sort((a, b) => toMs(b.styleProfile.updatedAt) - toMs(a.styleProfile.updatedAt))
  }

  // All updateStyleProfile system_logs, newest first. Low volume, so fetched in
  // one shot and grouped/filtered by userId client-side for per-user timelines.
  async getStyleUpdateLogs(max = 1000) {
    const q = query(collection(db, 'system_logs'), where('functionName', '==', 'updateStyleProfile'), limit(max))
    const snap = await getDocs(q)
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
  }
}

export { toMs as styleToMs }
